import { useState, type MouseEvent } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

interface TxnIdProps {
  /** The full transaction id (e.g. "VN-20260721…"). */
  value?: string | null;
  /** How many trailing characters to show when collapsed. */
  last?: number;
  className?: string;
}

/**
 * Compact transaction-id cell: shows only the last few characters to keep tables
 * tidy. Click it to expand to the full id at a larger size (with copy-to-clipboard);
 * click again to collapse.
 */
export function TxnId({ value, last = 5, className }: TxnIdProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!value) return <span className="text-fg-subtle">—</span>;

  const tail = value.length > last ? value.slice(-last) : value;

  const copy = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`${value} — click to expand`}
        className={cn(
          'group inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[11px] text-fg-subtle transition hover:bg-surface-subtle hover:text-fg',
          className,
        )}
      >
        <span aria-hidden className="opacity-50">…</span>
        <span>{tail}</span>
      </button>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex max-w-[22rem] items-center gap-1.5 rounded-md bg-surface-subtle px-2 py-1 ring-1 ring-border',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setOpen(false)}
        title="Click to collapse"
        className="select-text break-all text-left font-mono text-xs text-fg"
      >
        {value}
      </button>
      <button
        type="button"
        onClick={copy}
        aria-label="Copy transaction ID"
        title="Copy"
        className="shrink-0 text-fg-subtle transition hover:text-fg"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
      </button>
    </span>
  );
}
